import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { AuthValidators } from './validators/auth.validators';
import { Injectable } from '@angular/core';
import { LoginAction } from './base/login-action.enum';
import { LoginConfig } from './login.config';
import { LoginProvider } from './base/login-provider.interface';
import { ToastService } from '@capturum/ui/api';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class LoginService implements LoginProvider {
  public loginForm: FormGroup;
  public resetForm: FormGroup;
  public forgotForm: FormGroup;

  private action: BehaviorSubject<{ action: LoginAction, success: boolean, status: number }> = new BehaviorSubject(null);

  constructor(
    protected readonly config: LoginConfig,
    protected readonly router: Router,
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly toastService: ToastService,
    protected readonly translateService: TranslateService,
    protected readonly formBuilder: FormBuilder
  ) {
    this.prePopulateForms();
  }

  public setAction(action: LoginAction, success: boolean, status: number = 200): void {
    this.action.next({ action, success, status });
  }

  public getAction(): Observable<{ action: LoginAction, success: boolean, status: number }> {
    return this.action.asObservable();
  }

  public onLoginSuccess(response: any): void {
    const isTwoFactorResponse = response?.hasOwnProperty('method');

    if (response && isTwoFactorResponse) {
      // Let AuthService.onSuccessfulLogin() handle the redirect
    } else if (response && !isTwoFactorResponse) {
      this.router.navigate([this.activatedRoute?.snapshot?.queryParamMap?.get('redirectUrl') || this.config.redirectAfterLogin]);
    }
  }

  public onLoginFail(response: any): void {
  }

  public onForgotSuccess(response: any): void {
    this.toastService.success(
      this.translateService.instant('auth.forgot-password.confirmed.title'),
      this.translateService.instant('auth.forgot-password.confirmed.description')
    );
    this.router.navigate(['auth/login']);
  }

  public onForgotFail(response: any): void {
  }

  public onResetSuccess(response: any): void {
    this.toastService.success(
      this.translateService.instant('toast.success.title'),
      this.translateService.instant('detail.item.saved'),
    );
    this.router.navigate(['auth/login']);
  }

  public onResetFail(response: any): void {
  }

  public onTwoFactorLoginSuccess(response: any): void {
    if (response) {
      this.router.navigate([this.activatedRoute?.snapshot?.queryParamMap?.get('redirectUrl') || this.config.redirectAfterLogin]);
    }
  }

  public onTwoFactorLoginFail(response: any): void {
  }

  protected prePopulateForms(): void {
    this.loginForm = this.formBuilder.group({
      email: [null, Validators.compose([Validators.required, Validators.email])],
      password: [null, Validators.required],
      remember: [false]
    });
    this.resetForm = this.formBuilder.group({
      email: [null, Validators.compose([Validators.required, Validators.email])],
      password: [null, Validators.compose([Validators.required, this.config.passwordValidator])],
      password_confirmation: [null, Validators.compose([Validators.required, AuthValidators.equalsField('password')])]
    });
    this.forgotForm = this.formBuilder.group({
      email: [null, Validators.compose([Validators.required, Validators.email])]
    });
  }
}
